import type { QueryClient } from '@tanstack/react-query';
import { ROOTSTOCK_FINALITY_STABILITY_MS } from './scheduleActionStore';

/**
 * Refresh every data source that can reflect a schedule mutation: the API
 * queries (['schedules', ...]) and the wagmi getMyDcaSchedules contract reads.
 */
export function invalidateScheduleData(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['schedules'] });
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const [root, params] = query.queryKey as [unknown, ...unknown[]];
      if (root !== 'readContract') return false;
      const options = params as Record<string, unknown> | undefined;
      return options?.functionName === 'getMyDcaSchedules';
    },
  });
}

const RECONCILIATION_DELAYS_MS = [
  5_000,
  15_000,
  45_000,
  ROOTSTOCK_FINALITY_STABILITY_MS,
];
const scheduledHashes = new Set<string>();

/**
 * Bounded backoff refetches after a confirmed transaction, to nudge the
 * (possibly lagging) RPC data forward without aggressive polling. The final
 * refresh lands after the Rootstock API finality window so receipt-derived
 * overrides get a deterministic reconciliation opportunity. Fire and forget;
 * idempotent per transaction hash.
 */
export function scheduleReconciliationRefetches(queryClient: QueryClient, hash: string): void {
  if (scheduledHashes.has(hash)) return;
  scheduledHashes.add(hash);
  for (const delayMs of RECONCILIATION_DELAYS_MS) {
    setTimeout(() => {
      invalidateScheduleData(queryClient);
    }, delayMs);
  }
}
